import {
    ApiSmSpinV2ReqSchema as ApiSmSpinReqSchema,
    ApiSmSpinV2ResSchema as ApiSmSpinResSchema,
    ApiSmStateResSchema,
    type ApiSmSpinV2Res as ApiSmSpinRes,
    type ApiSmStateSnapshot,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useCallback, useEffect, useRef } from "react";
import { authenticatedApiCall } from "../helpers/api";
import {
    clientSpinResponse,
    mergeSpinResponseSnapshot,
    mergeSpinSnapshot,
    reconcileSlotSnapshot,
    type ClientSpinResponse,
} from "../helpers/slotMachineSnapshot";
import i18n from "../i18n/i18n";

/** Mark the authoritative slot snapshot stale from another feature. */
export function useRequestSlotMachineRefetch() {
    const { setSlice: setSlotMachineSlice } = useSlice("slotMachineSlice");

    return useCallback(() => {
        setSlotMachineSlice((current) => ({
            refreshRequestedRevision: current.refreshRequestedRevision + 1,
        }));
    }, [setSlotMachineSlice]);
}

export class SlotMachineApiError extends Error {
    readonly response?: Extract<ApiSmSpinRes, { status: "ERROR" }>;

    constructor(
        message: string,
        response?: Extract<ApiSmSpinRes, { status: "ERROR" }>,
    ) {
        super(message);
        this.name = "SlotMachineApiError";
        this.response = response;
    }
}

async function fetchSlotState(signal?: AbortSignal) {
    const { body } = await authenticatedApiCall(
        "/api/v1/sm/state?consistency=STRONG",
        ApiSmStateResSchema,
        { signal },
    );
    if (body.status === "ERROR") {
        throw new SlotMachineApiError(
            body.message ??
                i18n.t("errors.loadGameState", {
                    code: body.code,
                }),
        );
    }
    return body.snapshot;
}

function snapshotHasChaseField(snapshot: ApiSmStateSnapshot) {
    return Object.prototype.hasOwnProperty.call(snapshot, "chase");
}

export function useSlotMachine(enabled: boolean) {
    const { state: slotState, setSlice: setSlotMachineSlice } =
        useSlice("slotMachineSlice");
    const { setSlice: setCommerceSlice } = useSlice("commerceSlice");
    const mountedRef = useRef(false);
    const stateRequestRef = useRef<AbortController | null>(null);
    const hadCareerAvailabilityRef = useRef(false);
    const isSpinningRef = useRef(slotState.spinStatus === "spinning");
    isSpinningRef.current = slotState.spinStatus === "spinning";
    const snapshotIsComplete =
        slotState.snapshot !== null &&
        snapshotHasChaseField(slotState.snapshot) &&
        slotState.snapshot.campaign.betting !== undefined;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            stateRequestRef.current?.abort();
        };
    }, []);

    const refetch = useCallback(
        async (requestedRevision?: number) => {
            if (!enabled) return;

            const targetRevision =
                requestedRevision ?? slotState.refreshRequestedRevision;
            stateRequestRef.current?.abort();
            const controller = new AbortController();
            stateRequestRef.current = controller;
            if (mountedRef.current) {
                setSlotMachineSlice({
                    loadStatus: "loading",
                    loadError: null,
                });
            }

            try {
                const nextSnapshot = await fetchSlotState(controller.signal);
                if (
                    mountedRef.current &&
                    stateRequestRef.current === controller
                ) {
                    setSlotMachineSlice((current) => ({
                        snapshot: reconcileSlotSnapshot(
                            current.snapshot,
                            nextSnapshot,
                        ),
                        loadStatus: "ready",
                        loadError: null,
                        refreshCompletedRevision: Math.max(
                            current.refreshCompletedRevision,
                            targetRevision,
                        ),
                    }));
                }
            } catch (error) {
                if (
                    !controller.signal.aborted &&
                    mountedRef.current &&
                    stateRequestRef.current === controller
                ) {
                    setSlotMachineSlice({
                        loadStatus: "error",
                        loadError:
                            error instanceof Error
                                ? error.message
                                : String(error),
                    });
                }
            } finally {
                if (
                    mountedRef.current &&
                    stateRequestRef.current === controller
                ) {
                    stateRequestRef.current = null;
                }
            }
        },
        [enabled, setSlotMachineSlice, slotState.refreshRequestedRevision],
    );

    useEffect(() => {
        if (!enabled) return;
        const refreshOnResume = () => {
            if (
                document.visibilityState === "visible" &&
                !isSpinningRef.current
            ) {
                void refetch();
            }
        };
        document.addEventListener("visibilitychange", refreshOnResume);
        return () =>
            document.removeEventListener("visibilitychange", refreshOnResume);
    }, [enabled, refetch]);

    useEffect(() => {
        if (slotState.snapshot?.careerAvailability !== undefined) {
            hadCareerAvailabilityRef.current = true;
        } else if (
            enabled &&
            hadCareerAvailabilityRef.current &&
            !isSpinningRef.current
        ) {
            // Some reward endpoints still return the older slot shape. Refresh
            // once after such a replacement; never poll an unsupported server.
            hadCareerAvailabilityRef.current = false;
            void refetch();
        }
    }, [enabled, refetch, slotState.snapshot, slotState.spinStatus]);

    useEffect(() => {
        if (!enabled) {
            stateRequestRef.current?.abort();
            return;
        }
        if (!snapshotIsComplete) void refetch();
    }, [enabled, refetch, snapshotIsComplete]);

    useEffect(() => {
        if (!enabled) return;
        if (
            slotState.refreshRequestedRevision <=
            slotState.refreshCompletedRevision
        ) {
            return;
        }

        void refetch(slotState.refreshRequestedRevision);
    }, [
        enabled,
        refetch,
        slotState.refreshCompletedRevision,
        slotState.refreshRequestedRevision,
    ]);

    const spin = useCallback(async (betMultiplier: number = 1): Promise<ClientSpinResponse> => {
        if (!slotState.snapshot?.campaign.betting) {
            throw new SlotMachineApiError(i18n.t("errors.gameStateNotReady"));
        }

        const request = ApiSmSpinReqSchema.parse({
            requestId: crypto.randomUUID(),
            betMultiplier: String(betMultiplier),
            expectedBetPolicyId: slotState.snapshot.campaign.betting?.policyId,
            campaignId: slotState.snapshot.campaign.campaignId,
            expectedConfigVersion: slotState.snapshot.campaign.configVersion,
            expectedModelKey: slotState.snapshot.campaign.modelKey,
            expectedStateVersion: slotState.snapshot.player.stateVersion,
            expectedSettledSpinsCount:
                slotState.snapshot.player.settledSpinsCount,
        });
        stateRequestRef.current?.abort();
        stateRequestRef.current = null;
        if (mountedRef.current) {
            setSlotMachineSlice({
                loadStatus: "ready",
                spinStatus: "spinning",
                spinError: null,
            });
        }


        const callSpin = () =>
            authenticatedApiCall("/api/v2/sm/spin", ApiSmSpinResSchema, {
                method: "POST",
                body: JSON.stringify(request),
            });

        try {
            let response;
            let transportRetried = false;
            try {
                response = await callSpin();
            } catch {
                // A transport retry reuses the same requestId, so PostgreSQL
                // replays the stored terminal response instead of spinning twice.
                transportRetried = true;
                response = await callSpin();
            }

            const body = response.body;
            const completeSnapshot = body.replayed === true;
            if (body.status === "ERROR") {
                if (body.commercePresentation && mountedRef.current) {
                    setCommerceSlice({
                        activePresentation: body.commercePresentation,
                        checkoutStatus: "idle",
                        error: null,
                    });
                }
                const responseSnapshot = body.snapshot;
                if (responseSnapshot && mountedRef.current) {
                    setSlotMachineSlice((current) => ({
                        snapshot: mergeSpinSnapshot(
                            current.snapshot ?? undefined,
                            responseSnapshot,
                            null,
                            completeSnapshot,
                        ),
                    }));
                }
                throw new SlotMachineApiError(
                    body.message ??
                        i18n.t("errors.spinFailed", {
                            code: body.code,
                        }),
                    body,
                );
            }
            const result = clientSpinResponse(body, transportRetried);
            if (mountedRef.current) {
                setSlotMachineSlice((current) => ({
                    snapshot: mergeSpinResponseSnapshot(
                        current.snapshot ?? undefined,
                        result,
                    ),
                }));
            }
            return result;
        } catch (error) {
            if (mountedRef.current) {
                setSlotMachineSlice({
                    spinError:
                        error instanceof Error ? error.message : String(error),
                });
            }
            throw error;
        } finally {
            if (mountedRef.current) {
                setSlotMachineSlice({ spinStatus: "idle" });
            }
        }
    }, [setCommerceSlice, setSlotMachineSlice, slotState.snapshot]);

    return {
        snapshot: slotState.snapshot ?? undefined,
        isLoading: slotState.loadStatus === "loading",
        loadError: slotState.loadError,
        refetch,
        spin,
        isSpinning: slotState.spinStatus === "spinning",
        spinError: slotState.spinError,
    };
}
